/**
 * Ruler Components for Template Preview
 * Displays horizontal and vertical rulers with cm markings
 * NOW POSITIONED INSIDE THE PREVIEW (OVERLAY)
 */

interface HorizontalRulerProps {
  widthPx: number; // Width in pixels
}

export function HorizontalRuler({ widthPx }: HorizontalRulerProps) {
  const cmInPx = 37.795; // 1cm = 37.795px at 96 DPI
  const maxCm = Math.ceil(widthPx / cmInPx);
  
  return (
    <div 
      style={{ 
        position: 'relative',
        width: `${widthPx}px`, 
        height: '20px',
        backgroundColor: 'var(--ruler)',
        border: '1px solid hsl(var(--border))',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        fontSize: '9px',
        color: 'var(--ruler-foreground)'
      }}
    >
      {Array.from({ length: maxCm + 1 }).map((_, i) => {
        const cm = i;
        const pixelPos = cm * cmInPx;
        if (pixelPos > widthPx) return null;
        
        return (
          <div key={i}>
            {/* cm-Label - zentriert über dem Strich */}
            {cm > 0 && (
              <span 
                className="absolute" 
                style={{ 
                  left: `${pixelPos}px`,
                  top: '1px',
                  fontSize: '9px',
                  color: 'var(--ruler-foreground)',
                  lineHeight: '1',
                  transform: 'translateX(-50%)'
                }}
              >
                {cm}
              </span>
            )}
            
            {/* Hauptmarkierung alle 1cm */}
            <div 
              className="absolute" 
              style={{ 
                left: `${pixelPos}px`, 
                top: '12px',
                width: '1px', 
                height: cm === 0 ? '0px' : '8px',
                backgroundColor: 'var(--ruler-foreground)'
              }} 
            />
            
            {/* Kleine Markierungen alle 0.5cm */}
            {cm < maxCm && (
              <div 
                className="absolute" 
                style={{ 
                  left: `${pixelPos + cmInPx / 2}px`, 
                  top: '16px',
                  width: '1px', 
                  height: '4px',
                  backgroundColor: 'var(--muted-foreground)'
                }} 
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

interface VerticalRulerProps {
  heightPx: number;
}

export function VerticalRuler({ heightPx }: VerticalRulerProps) {
  const cmInPx = 37.795; // 1cm = 37.795px at 96 DPI
  const maxCm = Math.ceil(heightPx / cmInPx);
  
  return (
    <div 
      style={{ 
        position: 'relative',
        width: '20px',
        height: `${heightPx}px`,
        backgroundColor: 'var(--ruler)',
        border: '1px solid hsl(var(--border))',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        fontSize: '9px',
        color: 'var(--ruler-foreground)'
      }}
    >
      {Array.from({ length: maxCm + 1 }).map((_, i) => {
        const cm = i;
        const pixelPos = cm * cmInPx;
        if (pixelPos > heightPx) return null;
        
        return (
          <div key={i}>
            {/* cm-Label - rotiert, links neben dem Strich */}
            {cm > 0 && (
              <span 
                className="absolute" 
                style={{ 
                  left: '1px',
                  top: `${pixelPos}px`, 
                  fontSize: '9px',
                  color: 'var(--ruler-foreground)',
                  lineHeight: '1',
                  transform: 'translateY(-50%) rotate(0deg)',
                  transformOrigin: 'left center'
                }}
              >
                {cm}
              </span>
            )}
            
            {/* Hauptmarkierung alle 1cm */}
            <div 
              className="absolute" 
              style={{ 
                left: '12px',
                top: `${pixelPos}px`, 
                width: cm === 0 ? '0px' : '8px',
                height: '1px',
                backgroundColor: 'var(--ruler-foreground)'
              }} 
            />
            
            {/* Kleine Markierungen alle 0.5cm */}
            {cm < maxCm && (
              <div 
                className="absolute" 
                style={{ 
                  left: '16px',
                  top: `${pixelPos + cmInPx / 2}px`, 
                  width: '4px',
                  height: '1px',
                  backgroundColor: 'var(--muted-foreground)'
                }} 
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
